import {compact} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useCallback, useEffect, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {PrimaryExperimentalRoleType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  PRIMARY_ROLE_TO_DISPLAY_TEXT,
  ExperimentalRole,
  ExperimentalRolesByTypeMapping,
  ORDERED_PRIMARY_EXPERIMENTAL_ROLES,
  NULL_SUBROLE_KEY,
} from 'samples/experimental-roles/ExperimentalRoleConstants';
import {getRolesByRoleType} from 'samples/experimental-roles/ExperimentalRoleUtil';
import PrimaryExperimentalRoleSymbol from 'samples/experimental-roles/PrimaryExperimentalRoleSymbol';
import {InferAnnotationParamsResponse} from 'samples/plate-maps/annotate-plate-modal/ai-assistant/AnnotationAssistantDAO';
import AnnotationAssistantInput from 'samples/plate-maps/annotate-plate-modal/ai-assistant/AnnotationAssistantInput';
import ControlSubroleButtons from 'samples/plate-maps/annotate-plate-modal/ControlSubroleButtons';
import WellRoleGroupButtons from 'samples/plate-maps/annotate-plate-modal/WellRoleGroupButtons';
import AssignWellsByPattern, {
  PatternParametersInput,
} from 'samples/plate-maps/pattern-controls/AssignWellsByPattern';
import {
  PlateMap,
  PatternAssignmentParameters,
  WellAssignmentMethod,
  DEFAULT_PATTERN_PARAMS,
} from 'samples/plate-maps/PlateMapConstants';
import {applyEditToSelected, getPlateMapWithPatternPreview} from 'samples/plate-maps/PlateMapUtil';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE_LIGHTER, BLUE_POWDER_LIGHT, GRAY, GRAY_DARKER} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {RadioButtonSelector} from 'ui-library/inputs/RadioButtonGroup';
import {Tab, TabContainer} from 'ui-library/TabContainer';

interface WellRoleGroupAssignmentProps {
  primaryRole: PrimaryExperimentalRoleType;
  groupsPerRole: ExperimentalRolesByTypeMapping;
  onAssignGroup: (role: ExperimentalRole) => void;
  selectedSubrole: string | null;
  setSelectedSubrole: (subrole: string | null) => void;
  assignmentMethod: WellAssignmentMethod;
  setAssignmentMethod: (method: WellAssignmentMethod) => void;
  patternParams: PatternParametersInput;
  setPatternParams: (params: PatternParametersInput) => void;
  applyPattern: () => void;
  cannotApplyPattern: boolean;
}

/**
 * Options to assign well role groups on a plate map by specifying groups or with a pattern.
 */
function WellRoleGroupAssignment({
  primaryRole,
  onAssignGroup,
  selectedSubrole,
  setSelectedSubrole,
  groupsPerRole,
  applyPattern,
  assignmentMethod,
  setAssignmentMethod,
  patternParams,
  setPatternParams,
  cannotApplyPattern,
}: WellRoleGroupAssignmentProps): JSX.Element {
  return (
    <div>
      <div className={css(styles.methodRadioButtons)}>
        <RadioButtonSelector
          isVertical={true}
          selectedValue={assignmentMethod}
          options={[
            {
              label: 'Specify group',
              value: WellAssignmentMethod.SPECIFY_GROUP,
              dataTestAttrs: {
                component: 'WellRoleAssignmentSettings',
                element: 'AssignmentMethodRadioOption',
                key: WellAssignmentMethod.SPECIFY_GROUP,
              },
            },
            {
              label: 'Fill by pattern',
              value: WellAssignmentMethod.PATTERN,
              dataTestAttrs: {
                component: 'WellRoleAssignmentSettings',
                element: 'AssignmentMethodRadioOption',
                key: WellAssignmentMethod.PATTERN,
              },
            },
          ]}
          onChange={(method: WellAssignmentMethod) => {
            setAssignmentMethod(method);
          }}
        />
      </div>

      {assignmentMethod === WellAssignmentMethod.SPECIFY_GROUP && (
        <>
          {primaryRole === PrimaryExperimentalRoleType.CONTROL ? (
            <ControlSubroleButtons
              groupPopoverDisabled={false}
              onAssignGroup={onAssignGroup}
              groupsBySubrole={groupsPerRole[primaryRole] ?? {}}
            />
          ) : (
            <WellRoleGroupButtons
              roleGroups={groupsPerRole[primaryRole]?.[NULL_SUBROLE_KEY] ?? []}
              primaryRole={primaryRole}
              subrole={null}
              onAssignGroup={onAssignGroup}
            />
          )}
        </>
      )}

      {assignmentMethod === WellAssignmentMethod.PATTERN && (
        <div>
          {primaryRole === PrimaryExperimentalRoleType.CONTROL && (
            <ControlSubroleButtons
              groupPopoverDisabled={true}
              selectedSubrole={selectedSubrole}
              setSelectedSubrole={setSelectedSubrole}
            />
          )}
          <AssignWellsByPattern patternParams={patternParams} setPatternParams={setPatternParams} />
          <Button
            className={css(styles.applyButton)}
            text="Apply"
            size={ButtonSize.MEDIUM}
            buttonStyle={ButtonStyle.SECONDARY}
            onClick={applyPattern}
            disabled={cannotApplyPattern}
          />
        </div>
      )}
    </div>
  );
}

export interface WellRoleAssignmentSettingsProps {
  plateMap: PlateMap;
  onEditPlateMap: (
    plateMap: PlateMap,
    roleTrackingParams: {
      primaryRole?: PrimaryExperimentalRoleType;
      subrole?: string | null;
      annotatedByAi?: boolean;
    }
  ) => void;
  selectedWellIndices: Set<number>;
  deselectWells: () => void;
  setPlateMapWithPreview: (plateMap: PlateMap | null) => void;
  setIsAIPreview: (isAIPreview: boolean) => void;
  onSubmitAIPrompt: (userPrompt: string) => Promise<InferAnnotationParamsResponse>;
  onApplyAISuggestion: () => void;
  onDiscardAISuggestion: () => void;
  aiSuggestedPlateMap: PlateMap | null;
  setAISuggestedPlateMap: (plateMap: PlateMap | null) => void;
}

enum TabLabel {
  MANUAL = 'MANUAL',
  AI = 'AI',
}

/**
 * Settings to assign well roles to a plate by specifying groups or by pattern.
 */
export default function WellRoleAssignmentSettings({
  plateMap,
  onEditPlateMap,
  selectedWellIndices,
  deselectWells,
  setPlateMapWithPreview,
  setIsAIPreview,
  onSubmitAIPrompt,
  onApplyAISuggestion,
  onDiscardAISuggestion,
  aiSuggestedPlateMap,
  setAISuggestedPlateMap,
}: WellRoleAssignmentSettingsProps): JSX.Element {
  const rolesByType = getRolesByRoleType(compact(plateMap.wells.map((well) => well.role)));
  const [primaryRole, setPrimaryRole] = useState<PrimaryExperimentalRoleType | null>(null);
  const [subrole, setSubrole] = useState<string | null>(null);
  const [assignmentMethod, setAssignmentMethod] = useState(WellAssignmentMethod.SPECIFY_GROUP);
  const [patternParams, setPatternParams] = useState<PatternParametersInput>(DEFAULT_PATTERN_PARAMS);
  const [activeTab, setActiveTab] = useState<TabLabel>(TabLabel.MANUAL);
  const cannotApplyPattern =
    assignmentMethod !== WellAssignmentMethod.PATTERN ||
    patternParams.replicates == null ||
    patternParams.replicates === 0 ||
    patternParams.startingGroup == null ||
    patternParams.startingGroup === 0 ||
    primaryRole == null ||
    selectedWellIndices.size === 0;

  const getPlateMapWithPattern = useCallback(() => {
    if (cannotApplyPattern) {
      return null;
    }
    return getPlateMapWithPatternPreview(
      plateMap,
      (group: number) => ({role: {primaryRole, group, subrole}}),
      patternParams as PatternAssignmentParameters,
      selectedWellIndices
    );
  }, [primaryRole, cannotApplyPattern, patternParams, plateMap, selectedWellIndices, subrole]);

  useEffect(() => {
    if (activeTab === TabLabel.AI) {
      setAISuggestedPlateMap(null);
    }
    setPlateMapWithPreview(getPlateMapWithPattern());
  }, [setPlateMapWithPreview, getPlateMapWithPattern, activeTab, setAISuggestedPlateMap]);

  useEffect(() => {
    setIsAIPreview(false);
  }, [activeTab, setIsAIPreview]);

  // Assign the given role to all selected wells on the plate map
  function assignRoleToSelected(role: ExperimentalRole): void {
    if (selectedWellIndices.size === 0) {
      return;
    }
    onEditPlateMap(applyEditToSelected(plateMap, {role}, selectedWellIndices), {
      primaryRole: role.primaryRole,
      subrole: role.subrole,
    });
    deselectWells();
  }

  function applyPattern(): void {
    const updatedPlateMap = getPlateMapWithPattern();
    if (updatedPlateMap == null || selectedWellIndices.size === 0) {
      return;
    }
    onEditPlateMap(updatedPlateMap, {primaryRole: primaryRole!, subrole});
    setAssignmentMethod(WellAssignmentMethod.SPECIFY_GROUP);
    setPatternParams(DEFAULT_PATTERN_PARAMS);
    setPrimaryRole(null);
    setSubrole(null);
    deselectWells();
    setPlateMapWithPreview(null);
  }

  const manualAnnotatePanel = (
    <div>
      <p className={css(styles.helpText)}>Select wells in the plate, then select a role to apply to them</p>
      <div className={css(styles.roleOptionsContainer)}>
        {ORDERED_PRIMARY_EXPERIMENTAL_ROLES.map((role) => (
          <div
            key={role}
            className={classNames(css(styles.roleOption), css(styles.primaryRoleOption), {
              [css(styles.selectedRoleOption)]: primaryRole === role,
            })}
            onClick={() => {
              setPrimaryRole(role);
              setSubrole(null);
            }}
            {...getTestProps({
              component: 'WellRoleAssignmentSettings',
              element: 'WellRoleButton',
              key: role,
            })}
          >
            <PrimaryExperimentalRoleSymbol
              radius={10}
              primaryRole={role}
              label={PRIMARY_ROLE_TO_DISPLAY_TEXT[role].abbreviation}
            />
            <p className={css(styles.roleText)}>{PRIMARY_ROLE_TO_DISPLAY_TEXT[role].displayName}</p>
          </div>
        ))}
      </div>
      {primaryRole != null && (
        <WellRoleGroupAssignment
          primaryRole={primaryRole}
          groupsPerRole={rolesByType}
          setAssignmentMethod={setAssignmentMethod}
          onAssignGroup={assignRoleToSelected}
          applyPattern={applyPattern}
          assignmentMethod={assignmentMethod}
          patternParams={patternParams}
          setPatternParams={setPatternParams}
          selectedSubrole={subrole}
          setSelectedSubrole={setSubrole}
          cannotApplyPattern={cannotApplyPattern}
        />
      )}
    </div>
  );

  if (!BenchlingConfig.INVENTORY_ENABLE_AI_PLATE_MAP_ANNOTATION_ASSISTANT) {
    return manualAnnotatePanel;
  }

  return (
    <TabContainer<TabLabel> activeTab={activeTab} onRequestTabChange={setActiveTab}>
      <Tab<TabLabel> title="Annotate manually" tabKey={TabLabel.MANUAL}>
        {manualAnnotatePanel}
      </Tab>
      <Tab<TabLabel>
        title={
          <div>
            Plate Annotator
            <BenchlingIcon icon="sparkle" color="purple" style={{marginLeft: 4}} />
          </div>
        }
        tabKey={TabLabel.AI}
      >
        <AnnotationAssistantInput
          onSubmitAIPrompt={onSubmitAIPrompt}
          onApplyAISuggestion={onApplyAISuggestion}
          onDiscardAISuggestion={onDiscardAISuggestion}
          aiSuggestedPlateMap={aiSuggestedPlateMap}
        />
      </Tab>
    </TabContainer>
  );
}

const styles = StyleSheet.create({
  helpText: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARKER,
  },
  roleOptionsContainer: {
    display: 'flex',
    gap: 8,
    marginBottom: 24,
  },
  roleOption: {
    flex: 1,
    borderRadius: 4,
    cursor: 'pointer',
    textAlign: 'center',
    border: `1px solid ${GRAY}`,
    ':hover': {
      backgroundColor: BLUE_POWDER_LIGHT,
    },
  },
  primaryRoleOption: {
    paddingTop: 4,
    width: 70,
  },
  selectedRoleOption: {
    borderColor: BLUE_LIGHTER,
    backgroundColor: BLUE_POWDER_LIGHT,
  },
  roleText: {
    marginBottom: 4,
  },
  methodRadioButtons: {
    marginBottom: 16,
  },
  applyButton: {
    marginTop: 24,
    float: 'right',
  },
});
